import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import type { useLocation } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";

interface SwipeIndicatorsProps {
  isSwipingList: () => boolean;
  swipeListTx: () => number;
  isSwipingListY: () => boolean;
  swipeListTy: () => number;
  LIST_SWIPE_INDICATOR_TRIGGER_PX: number;
  LIST_SWIPE_COMMIT_THRESHOLD_PX: number;
  location: ReturnType<typeof useLocation>;
  getAdjacentListNames: () => { prev: string; next: string };
}

const truncateListName = (name: string) => {
  const max = 20;
  return name.length > max ? `${name.slice(0, max)}...` : name;
};

export function SwipeIndicators(props: SwipeIndicatorsProps) {
  const { t } = useI18n();
  const SWIPE_OPACITY_FADE_DISTANCE_PX = 30;
  const SWIPE_DAMPING_FACTOR = 0.3;
  const SWIPE_MAX_OFFSET_PX = 40;

  return (
    <>
      {/* Left list swipe commit indicator */}
      <Show
        when={props.isSwipingList() && props.swipeListTx() > props.LIST_SWIPE_INDICATOR_TRIGGER_PX}
      >
        <div
          class="fixed left-4 top-1/2 -translate-y-1/2 z-[200] flex items-center gap-2 rounded-full border px-3 py-1.5 shadow-lg bg-background/95 backdrop-blur-md transition-all duration-300 pointer-events-none max-w-[280px]"
          classList={{
            "border-border/30 text-muted-foreground scale-100":
              props.swipeListTx() <= props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
            "border-primary/50 text-primary bg-primary/5 scale-105 shadow-primary/10":
              props.swipeListTx() > props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
          }}
        >
          <span class="inline-block transition-transform duration-300 text-sm animate-pulse shrink-0">
            ←
          </span>
          <span class="text-[10px] font-semibold tracking-wide truncate block whitespace-nowrap">
            {props.swipeListTx() > props.LIST_SWIPE_COMMIT_THRESHOLD_PX
              ? `${t(msg`Release to view`)} '${truncateListName(props.getAdjacentListNames().prev)}'`
              : `${t(msg`Swipe right for`)} '${truncateListName(props.getAdjacentListNames().prev)}'`}
          </span>
        </div>
      </Show>

      {/* Right list swipe commit indicator */}
      <Show
        when={props.isSwipingList() && props.swipeListTx() < -props.LIST_SWIPE_INDICATOR_TRIGGER_PX}
      >
        <div
          class="fixed right-4 top-1/2 -translate-y-1/2 z-[200] flex items-center gap-2 rounded-full border px-3 py-1.5 shadow-lg bg-background/95 backdrop-blur-md transition-all duration-300 pointer-events-none max-w-[280px]"
          classList={{
            "border-border/30 text-muted-foreground scale-100":
              Math.abs(props.swipeListTx()) <= props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
            "border-primary/50 text-primary bg-primary/5 scale-105 shadow-primary/10":
              Math.abs(props.swipeListTx()) > props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
          }}
        >
          <span class="text-[10px] font-semibold tracking-wide truncate block whitespace-nowrap">
            {Math.abs(props.swipeListTx()) > props.LIST_SWIPE_COMMIT_THRESHOLD_PX
              ? `${t(msg`Release to view`)} '${truncateListName(props.getAdjacentListNames().next)}'`
              : `${t(msg`Swipe left for`)} '${truncateListName(props.getAdjacentListNames().next)}'`}
          </span>
          <span class="inline-block transition-transform duration-300 text-sm animate-pulse shrink-0">
            →
          </span>
        </div>
      </Show>

      {/* Top vertical pull down list swipe commit indicator */}
      <Show
        when={
          props.isSwipingListY() &&
          props.swipeListTy() > props.LIST_SWIPE_INDICATOR_TRIGGER_PX &&
          props.location().pathname !== "/"
        }
      >
        <div
          class="fixed top-[calc(var(--safe-area-inset-top)+1rem)] left-1/2 -translate-x-1/2 z-[200] flex flex-col items-center gap-1.5 pointer-events-none"
          style={{
            transform: `translate3d(0, ${Math.min(props.swipeListTy() * SWIPE_DAMPING_FACTOR, SWIPE_MAX_OFFSET_PX)}px, 0)`,
            opacity: `${Math.min(props.swipeListTy() / SWIPE_OPACITY_FADE_DISTANCE_PX, 1)}`,
          }}
        >
          <div
            class="h-1 w-12 rounded-full transition-all duration-300"
            classList={{
              "bg-muted-foreground/30": props.swipeListTy() <= props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
              "bg-primary w-16 h-1.5": props.swipeListTy() > props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
            }}
          />
          <div
            class="transition-all duration-300 flex items-center justify-center px-4 py-2"
            style={{
              opacity: props.swipeListTy() > props.LIST_SWIPE_INDICATOR_TRIGGER_PX ? "1" : "0",
              "margin-top": "4px",
            }}
          >
            <div
              class="flex items-center gap-1.5 rounded-full border px-3 py-1 bg-background/95 backdrop-blur-md shadow-lg text-[10px] font-semibold tracking-wide transition-all duration-300"
              classList={{
                "border-border/30 text-muted-foreground scale-100":
                  props.swipeListTy() <= props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
                "border-primary/50 text-primary bg-primary/5 scale-105 shadow-primary/10":
                  props.swipeListTy() > props.LIST_SWIPE_COMMIT_THRESHOLD_PX,
              }}
            >
              <span
                class="inline-block transition-transform duration-300 text-xs"
                style={{
                  transform:
                    props.swipeListTy() > props.LIST_SWIPE_COMMIT_THRESHOLD_PX
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                }}
              >
                ↓
              </span>
              <span>
                {props.swipeListTy() > props.LIST_SWIPE_COMMIT_THRESHOLD_PX
                  ? t(msg`Release to view Dashboard`)
                  : t(msg`Pull down to view Dashboard`)}
              </span>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}
